import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FiSearch, FiPackage, FiAlertCircle } from "react-icons/fi";
import { ordersAPI } from "../../services/api";
import OrderTracking from "./OrderTracking";
import LoadingSpinner from "../LoadingSpinner";

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #2c3e50;
  margin-bottom: 1rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #6c757d;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const SearchContainer = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  margin-bottom: 2rem;
`;

const SearchForm = styled.form`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 1rem 1.5rem;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &::placeholder {
    color: #adb5bd;
  }
`;

const SearchButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  min-width: 140px;
  justify-content: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const HelpText = styled.div`
  color: #6c757d;
  font-size: 0.9rem;
  text-align: center;
  margin-top: 1rem;
`;

const ErrorMessage = styled(motion.div)`
  background: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #f5c6cb;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const ExampleContainer = styled.div`
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 12px;
  margin-top: 1rem;
`;

const ExampleTitle = styled.h3`
  color: #495057;
  margin-bottom: 1rem;
  font-size: 1rem;
`;

const ExampleList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ExampleItem = styled.li`
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: "•";
    color: #007bff;
    font-weight: bold;
  }
`;

const TrackingSearch = () => {
  const [trackingNumber, setTrackingNumber] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!trackingNumber.trim()) {
      setError("Please enter a tracking number");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setOrder(null);

      const response = await ordersAPI.trackOrder(trackingNumber.trim());
      setOrder(response.data.data.order);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Order not found. Please check your tracking number."
      );
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Track Your Order</Title>
        <Subtitle>
          Enter your tracking number to get real-time updates on your meal
          delivery
        </Subtitle>
      </Header>

      <SearchContainer>
        <SearchForm onSubmit={handleSubmit}>
          <SearchInput
            type="text"
            placeholder="Enter tracking number (e.g., NG12345678)"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            disabled={loading}
          />
          <SearchButton type="submit" disabled={loading}>
            {loading ? (
              <LoadingSpinner size="small" />
            ) : (
              <>
                <FiSearch />
                Track Order
              </>
            )}
          </SearchButton>
        </SearchForm>

        {error && (
          <ErrorMessage
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <FiAlertCircle />
            {error}
          </ErrorMessage>
        )}

        <HelpText>
          Your tracking number was provided when your order was confirmed
        </HelpText>

        <ExampleContainer>
          <ExampleTitle>Tracking Number Examples:</ExampleTitle>
          <ExampleList>
            <ExampleItem>
              NG12345678 (8-character alphanumeric code)
            </ExampleItem>
            <ExampleItem>
              Starts with "NG" followed by letters and numbers
            </ExampleItem>
            <ExampleItem>Found in your order confirmation email</ExampleItem>
          </ExampleList>
        </ExampleContainer>
      </SearchContainer>

      {loading && (
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      )}

      {order && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <OrderTracking trackingNumber={trackingNumber} />
        </motion.div>
      )}
    </Container>
  );
};

export default TrackingSearch;
